import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Loader2, Upload, FileText, ExternalLink, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { loanProfilingApi } from '@/services/api';
import { inr } from './CockpitUI';

const TREATMENTS = [
  { value: 'DEDUCTED', label: 'Deducted at disbursal' },
  { value: 'UPFRONT', label: 'Customer pays upfront' },
  { value: 'ADDED_TO_LOAN', label: 'Added to the loan' },
];
const CHARGED_BY = [
  { value: 'BANK', label: 'Bank' },
  { value: 'WISEDRIVE', label: 'Wisedrive' },
];

const COMMON_CHARGES = [
  { label: 'Processing fee' }, { label: 'Stamp duty' }, { label: 'Documentation charges' },
  { label: 'Loan Suraksha', treatment: 'ADDED_TO_LOAN' }, { label: 'Car valuation charges' },
  { label: 'RTO / RTA charges', treatment: 'UPFRONT' },
  { label: 'Wisedrive service fee', charged_by: 'WISEDRIVE', treatment: 'UPFRONT', gst_pct: 18 },
];

export const Field = ({ label, children, hint, className = '' }) => (
  <div className={className}>
    <Label className="text-xs font-medium text-slate-600">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
  </div>
);

export const Picker = ({ value, onChange, options, testId, placeholder, disabled }) => (
  <select value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={testId}
    className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-2.5 text-sm disabled:bg-slate-50 disabled:text-slate-400
      focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400">
    {placeholder && <option value="">{placeholder}</option>}
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

export const StatusPill = ({ status, testId }) => {
  const map = {
    DRAFT: ['bg-slate-100 text-slate-600', 'Draft'],
    SHARED: ['bg-blue-50 text-blue-700 border border-blue-100', 'Sent to customer'],
    ACCEPTED: ['bg-emerald-50 text-emerald-700 border border-emerald-200', "Customer's choice"],
    NOT_SELECTED: ['bg-slate-50 text-slate-400 border border-slate-200', 'Not selected'],
    DISBURSED: ['bg-violet-50 text-violet-700 border border-violet-200', 'Disbursed'],
  };
  const [cls, label] = map[status] || map.DRAFT;
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`} data-testid={testId}>{label}</span>;
};

/** Upload a file against this lead and hand the saved document back. */
export const OfferUpload = ({ leadId, docType, label, onUploaded, testId, busyLabel }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await loanProfilingApi.uploadDocument(leadId, docType, file);
      await onUploaded(res.data.document);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = '';
    }
  };

  return (
    <>
      <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" onChange={pick} data-testid={`${testId}-file`} />
      <Button type="button" size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={busy} data-testid={testId}>
        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
        {busy ? (busyLabel || 'Uploading…') : label}
      </Button>
    </>
  );
};

export const DocLink = ({ leadId, docId, name, testId }) => (
  <a href={loanProfilingApi.fileUrl(leadId, docId)} target="_blank" rel="noreferrer" data-testid={testId}
    className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline max-w-[220px]">
    <FileText className="w-3.5 h-3.5 shrink-0" />
    <span className="truncate">{name || 'View file'}</span>
    <ExternalLink className="w-3 h-3 shrink-0" />
  </a>
);

/** The charge sheet: what the bank levies, what we add, and how each one is settled. */
export const OfferCharges = ({ leadId, offer, onSaved, readOnly }) => {
  const [rows, setRows] = useState(() => (offer.charges || []).map((c) => ({ ...c })));
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(rows) !== JSON.stringify(offer.charges || []);

  // follow the saved charge sheet whenever the offer changes under us
  useEffect(() => { setRows((offer.charges || []).map((c) => ({ ...c }))); /* eslint-disable-next-line */ }, [offer.updated_at]);

  const upd = (i, k, v) => setRows((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const add = (preset = {}) => setRows((p) => [...p, {
    label: '', amount: 0, charged_by: 'BANK', treatment: 'DEDUCTED', ...preset,
  }]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = rows.filter((r) => (r.label || '').trim()).map((r) => ({
        ...r, amount: parseFloat(r.amount) || 0,
        gst_pct: r.gst_pct === '' || r.gst_pct === null ? null : parseFloat(r.gst_pct) || null,
      }));
      const res = await loanProfilingApi.saveOfferCharges(leadId, offer.id, clean);
      onSaved(res.data);
      toast.success('Charges saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save the charges');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid={`offer-charges-${offer.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="text-sm font-semibold text-slate-800">Charges &amp; fees</h4>
          <p className="text-[11px] text-slate-400">Mark how each one is settled — it drives the letter and the net disbursement.</p>
        </div>
        {!readOnly && dirty && (
          <Button size="sm" onClick={save} disabled={saving} data-testid={`offer-charges-save-${offer.id}`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}Save charges
          </Button>
        )}
      </div>

      {rows.length === 0 && (
        <p className="text-xs text-slate-400 py-2" data-testid={`offer-charges-empty-${offer.id}`}>
          No charges on this offer yet — some banks levy none.
        </p>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-slate-400 text-left">
                <th className="py-1.5 pr-2 font-medium">Charge</th>
                <th className="py-1.5 px-2 font-medium w-28">Amount</th>
                <th className="py-1.5 px-2 font-medium w-20">GST %</th>
                <th className="py-1.5 px-2 font-medium w-32">Charged by</th>
                <th className="py-1.5 px-2 font-medium w-56">Settled how</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id || i} className="border-t border-slate-100">
                  <td className="py-1.5 pr-2">
                    <Input value={r.label || ''} disabled={readOnly} onChange={(e) => upd(i, 'label', e.target.value)}
                      className="h-9" placeholder="e.g. Processing fee" data-testid={`charge-label-${offer.id}-${i}`} />
                  </td>
                  <td className="py-1.5 px-2">
                    <Input type="number" value={r.amount ?? ''} disabled={readOnly} onChange={(e) => upd(i, 'amount', e.target.value)}
                      className="h-9" data-testid={`charge-amount-${offer.id}-${i}`} />
                  </td>
                  <td className="py-1.5 px-2">
                    <Input type="number" value={r.gst_pct ?? ''} disabled={readOnly} onChange={(e) => upd(i, 'gst_pct', e.target.value)}
                      className="h-9" placeholder="—" data-testid={`charge-gst-${offer.id}-${i}`} />
                  </td>
                  <td className="py-1.5 px-2">
                    <Picker value={r.charged_by} onChange={(v) => upd(i, 'charged_by', v)} options={CHARGED_BY}
                      disabled={readOnly} testId={`charge-by-${offer.id}-${i}`} />
                  </td>
                  <td className="py-1.5 px-2">
                    <Picker value={r.treatment} onChange={(v) => upd(i, 'treatment', v)} options={TREATMENTS}
                      disabled={readOnly} testId={`charge-treatment-${offer.id}-${i}`} />
                  </td>
                  <td className="py-1.5">
                    {!readOnly && (
                      <button onClick={() => setRows((p) => p.filter((_, idx) => idx !== i))}
                        className="p-1.5 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600"
                        data-testid={`charge-remove-${offer.id}-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!readOnly && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
          <Button size="sm" variant="outline" onClick={() => add()} data-testid={`charge-add-${offer.id}`}>
            <Plus className="w-3.5 h-3.5 mr-1" />Add a charge
          </Button>
          {COMMON_CHARGES.filter((c) => !rows.some((r) => r.label === c.label)).map((c) => (
            <button key={c.label} onClick={() => add(c)} data-testid={`charge-quick-${c.label.replace(/[^a-z]+/gi, '-').toLowerCase()}-${offer.id}`}
              className="text-[11px] px-2 py-1 rounded-full border border-dashed border-slate-300 text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors">
              + {c.label}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-3 border-t border-slate-100">
        {[['Deducted at disbursal', offer.charges_deducted], ['Customer pays upfront', offer.charges_upfront],
          ['Added to the loan', offer.charges_added_to_loan], ['Net to the dealer', offer.net_disbursement]].map(([l, v]) => (
          <div key={l}>
            <div className="text-[11px] text-slate-400">{l}</div>
            <div className="text-sm font-semibold text-slate-800 tabular-nums">{inr(v)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export { TREATMENTS, CHARGED_BY, Sparkles };
